<script>
import { mapActions } from 'vuex'
import { required, requiredIf } from 'vuelidate/lib/validators'
import moment from 'moment'
import reportsMixin from '@/modules/Reports/reportsMixin'
import { solutionTypeFormatter } from '@/support/utils/solutionTypeFormatter'

export default {
  name: 'ReportSurveyAnswersList',
  components: {
    Action: () => import('@/components/general/Action'),
    ActionBar: () => import('@/components/general/ActionBar'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    Datepicker: () => import('@/components/general/Datepicker'),
    FormSection: () => import('@/components/general/FormSection'),
    SelectField: () => import('@/components/general/SelectField'),
    ModalConfirm: () => import('@/components/macro/ModalConfirm')
  },

  mixins: [ reportsMixin ],
  data () {
    return {
      pagination: {
        query: {},
        filter: {},
        order: {},
        limit: 9999
      },

      formData: {
        dateInit: '',
        dateEnd: '',
        solutionType: null,
        solutions: [],
        learningPath: [],
        programs: [],
        tools: [],
        survey: null,
        reportId: null,
        type: 'satisfaction'
      },

      foundSurveys: null,
      toolTypes: [
        {
          text: 'Solução',
          value: 'solution'
        },
        {
          text: 'Programa',
          value: 'program'
        }
      ],

      toolTypesSatisfaction: [
        {
          text: 'Solução',
          value: 'solution'
        },
        {
          text: 'Programa',
          value: 'program'
        },
        {
          text: 'Ferramenta',
          value: 'tool'
        }
      ],

      surveyTypes: [
        {
          value: 'satisfaction',
          text: this.$t('management.survey.type:satisfaction')
        },
        {
          value: 'subscription',
          text: this.$t('management.survey.type:subscription')
        },
        {
          value: 'external',
          text: this.$t('management.survey.type:external')
        }
      ],

      toolTypeSelected: null,
      allProgramsList: [],
      filteredProgramsList: [],
      allToolsList: [],
      filteredToolsList: [],
      solutionTypeList: [],
      allSolutionsList: [],
      filteredSolutionsList: [],
      paths: [],
      modalWarning: false
    }
  },

  computed: {
    solutionsListPaginated () {
      return this.$store.getters.solutionsList
    },

    programsListPaginated () {
      return this.$store.getters.programsList
    },

    disabledSolution () {
      return !this.formData.solutionType || !this.formData.survey
    },

    disabledProgram () {
      return !this.formData.survey
    },

    getSolutionType () {
      return this.solutionTypeList.filter(solutionType => {
        return solutionType.value === this.formData.solutionType
      })[0]
    },

    showDatepicker () {
      return (this.toolTypeSelected === 'program' || this.formData.solutionType || this.toolTypeSelected === 'tool') || this.formData.type === 'external'
    },

    surveyName () {
      if (this.formData.type === 'external') {
        return 'global:form.external.survey'
      } else if (this.formData.type === 'subscription') {
        return 'global:form.subscription.survey'
      } else {
        return 'global:form.survey'
      }
    }
  },

  validations: {
    toolTypeSelected: {
      required: requiredIf(function () {
        return this.formData.type !== 'external'
      })
    },

    formData: {
      type: { required },

      solutionType: {
        required: requiredIf(function () {
          return this.toolTypeSelected === 'solution' && this.formData.type !== 'external'
        })
      },

      solutions: {
        required: requiredIf(function () {
          return this.toolTypeSelected === 'solution' && this.formData.type !== 'external'
        })
      },

      programs: {
        required: requiredIf(function () {
          return this.toolTypeSelected === 'program' && this.formData.type !== 'external'
        })
      },

      tools: {
        required: requiredIf(function () {
          return this.toolTypeSelected === 'tool' && this.formData.type === 'satisfaction'
        })
      },

      survey: {
        required
      },

      dateInit: {
        required
      },

      dateEnd: {
        required
      }
    }
  },

  watch: {
    'formData.survey' () {
      switch (this.toolTypeSelected) {
        case 'solution':
          if (!this.formData.solutionType) {
            return
          }

          this.getSolutionsByTypeAndSurvey()
          break
        case 'program':
          this.getProgramsBySurvey()
          break
        case 'tool':
          this.getToolsBySurvey()
          break
      }
    },

    'formData.solutionType' () {
      if (!this.formData.survey) {
        return
      }

      this.getSolutionsByTypeAndSurvey()
    },

    'formData.type' () {
      this.getSurveys()
    }
  },

  created () {
    this.setFetching(true)

    Promise.all([
      this.getSolutionsTypesList(),
      this.getLearningPathsList(),
      this.getSurveys()
    ]).finally(() => {
      this.setFetching(false)
    })
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptGetSolutionsTypesList',
      'attemptGetSolutionsList',
      'attemptGetProgramsList',
      'attemptGetLearningPathsList',
      'attemptGetToolsList',
      'attemptGetReportListSurveys',
      'attemptGetSurveyList',
      'attemptCheckReport',
      'attemptReportDownload'
    ]),

    getReportSurveyList () {
      this.$v.$touch()
      console.log(this.$v.formData)
      if (this.$v.$invalid) return
      const initDate = moment(this.formData.dateInit)
      const finalDate = moment(this.formData.dateEnd)

      if (finalDate.diff(initDate, 'days') > 31) {
        this.modalWarning = true
      } else {
        this.generateReport()
      }
    },

    generateReport () {
      this.modalWarning = false
      const params = {
        surveyType: this.formData.type,
        createdDateInit: this.formData.dateInit,
        createdDateEnd: this.formData.dateEnd,
        surveyId: this.formData.survey,
        solutionIds: this.formData.solutions,
        programIds: this.formData.programs,
        toolIds: this.formData.tools
      }

      this.setFetching(true)

      this.attemptGetReportListSurveys(params).then((response) => {
        this.reportId = response.data.id
        this.checkReport()
      }).catch(() => {
        this.setFeedback({ message: this.$t('reports.error:failed.to.fetch') })
      })
    },

    getLearningPathsList () {
      return this.attemptGetLearningPathsList({ limit: 300 }).then(pagination => {
        if (pagination.data) {
          this.paths = pagination.data.map((element) => {
            return {
              text: element.name,
              value: element.id,
              solutions: element.learningPathSolutions
            }
          })
        }
      })
    },

    getSolutionsLearningPath (learningPath) {
      learningPath.solutions.map((solution) => {
        if (!this.formData.solutions.includes(solution.solution.id)) {
          this.formData.solutions.push(solution.solution.id)
        }

        return solution
      })
    },

    getSolutionsTypesList () {
      this.setFetching(true)
      const pagination = {
        filter: {
          active: true
        },

        order: {
          name: 'asc'
        }
      }

      this.attemptGetSolutionsTypesList(pagination).then(pagination => {
        if (pagination.data) {
          let solutionTypes = pagination.data

          solutionTypes = this.convertSolutionTypesGroups(solutionTypes)

          solutionTypes.forEach(element => {
            this.solutionTypeList.push({
              text: solutionTypeFormatter(element),
              value: element.alias,
              id: element.id,
              group: element.group
            })
          })

          return true
        }
      }).finally(() => {
        this.setFetching(false)
      })
    },

    getSolutions (pagination) {
      return new Promise((resolve) => {
        this.attemptGetSolutionsList(pagination).then((paginationResponse) => {
          this.allSolutionsList.push(...this.solutionsListPaginated)

          if (pagination.page < paginationResponse.lastPage) {
            pagination.page += 1
            resolve(this.getSolutions(pagination))
          } else {
            resolve()
          }
        })
      })
    },

    getPrograms (pagination) {
      return new Promise((resolve) => {
        this.attemptGetProgramsList(pagination).then((paginationResponse) => {
          this.allProgramsList.push(...this.programsListPaginated)

          if (pagination.page < paginationResponse.lastPage) {
            pagination.page += 1
            resolve(this.getPrograms(pagination))
          } else {
            resolve()
          }
        })
      })
    },

    getTools (pagination) {
      return new Promise((resolve) => {
        this.attemptGetToolsList(pagination).then(({ data }) => {
          console.log(data)
          this.allToolsList.push(...data.data)

          if (pagination.page < data.lastPage) {
            pagination.page += 1
            resolve(this.getTools(pagination))
          } else {
            resolve()
          }
        })
      })
    },

    getSurveys () {
      this.setFetching(true)

      const search = this.pagination

      search.filter.survey_type = this.formData.type
      search.filter.has_answers = true

      this.attemptGetSurveyList(search).then(({ data }) => {
        this.foundSurveys = data.data.map(survey => {
          return {
            text: survey.name,
            value: survey.id
          }
        }) || []
      }).finally(() => {
        this.setFetching(false)
      }).catch(() => {
        this.foundSurveys = []
      })
    },

    getSolutionsByTypeAndSurvey () {
      this.filteredSolutionsList = []
      this.allSolutionsList = []
      this.formData.solutions = []
      this.formData.learningPath = []
      this.formData.programs = []

      if (!this.formData.solutionType) {
        return
      }

      const solutionType = this.getSolutionType
      const solutionsfilter = solutionType.group !== null
        ? { solution_type_group: solutionType.group }
        : { solution_type: solutionType.id }

      solutionsfilter.survey_id = this.formData.survey

      this.setFetching(true)

      this.getSolutions({
        filter: solutionsfilter,
        order: { name: 'ASC' },
        limit: 9999,
        page: 1
      }).finally(() => {
        this.allSolutionsList.forEach((solution) => {
          this.filteredSolutionsList.push({
            text: solution.name,
            value: solution.id
          })
        })

        this.setFetching(false)
      })
    },

    getProgramsBySurvey () {
      this.allProgramsList = []
      this.formData.programs = []
      this.formData.solutions = []
      this.formData.learningPath = []
      this.filteredProgramsList = []

      const programsFilter = { survey_id: this.formData.survey }

      this.setFetching(true)

      this.getPrograms({
        filter: programsFilter,
        order: { name: 'ASC' },
        limit: 9999,
        page: 1
      }).finally(() => {
        this.allProgramsList.forEach((program) => {
          this.filteredProgramsList.push({
            text: program.name,
            value: program.id
          })
        })

        this.setFetching(false)
      })
    },

    getToolsBySurvey () {
      this.allToolsList = []
      this.formData.tools = []
      this.formData.programs = []
      this.formData.solutions = []
      this.formData.learningPath = []
      this.filteredToolsList = []

      const toolsFilter = { survey_id: this.formData.survey }

      this.setFetching(true)

      this.getTools({
        filter: toolsFilter,
        order: { title: 'ASC' },
        limit: 9999,
        page: 1
      }).finally(() => {
        this.allToolsList.forEach((tool) => {
          this.filteredToolsList.push({
            text: tool.title,
            value: tool.id
          })
        })

        this.setFetching(false)
      })
    },

    closeModal () {
      this.modalWarning = false
    },

    leave () {
      this.$router.push({ name: 'reports.index' })
    },

    downloadReport () {
      this.attemptReportDownload(this.reportId).then((response) => {
        const url = window.URL.createObjectURL(new Blob([ response.data ]))
        const link = document.createElement('a')

        link.href = url

        link.setAttribute(
          'download',
          this.$t('reports.generate:report.survey.answers.list.file.name').toString()
        )

        document.body.appendChild(link)
        link.click()
      }).catch(() => {
        this.setFeedback({ message: this.$t('reports.error:failed.to.fetch') })
      })
    },

    checkReport () {
      const interval = setInterval(() => {
        this.attemptCheckReport(this.reportId).then((response) => {
          if (response.data.status === 'generated') {
            this.setFetching(false)
            this.downloadReport()
            clearInterval(interval)
          } else if (response.data.status === 'failed') {
            this.setFetching(false)
            this.setFeedback({ message: this.$t('reports.error:failed.to.fetch') })
            clearInterval(interval)
          }
        })
      }, 5000)
    }
  }
}
</script>

<template>
  <div class="main-content reports-create">
    <ContentHeader
      :title="$t('reports.generate:report.surveys')"
      light-theme
      fixed
    >
      <Action
        slot="back"
        type="button"
        :text="$t('global:back.reports')"
        class="btn-back"
        icon="keyboard_backspace"
        @click="leave"
      />
      <ActionBar slot="actionbar" />
    </ContentHeader>
    <div class="main-content-inner">
      <div class="center">
        <div class="solutions-create-header">
          <h2
            class="solutions-create-title"
            v-html="$t('reports.generate:header.description')"
          />
        </div>
        <form class="form">
          <FormSection :title="$t('global:form.filters')">
            <SelectField
              v-model="formData.type"
              :label="$t('global:form.management.survey.type')"
              :items="surveyTypes"
              :validation="$v.formData.type"
            />
            <SelectField
              v-if="formData.type === 'external'"
              v-model="formData.survey"
              :label="$t(surveyName)"
              :items="foundSurveys"
              :validation="$v.formData.survey"
            />
            <div v-if="formData.type !== 'external'">
              <SelectField
                v-model="toolTypeSelected"
                :label="$t('global:form.survey.tool.type')"
                :items="formData.type !== 'satisfaction' ? toolTypes : toolTypesSatisfaction"
                :validation="$v.toolTypeSelected"
              />
              <SelectField
                v-if="toolTypeSelected === 'solution'"
                v-model="formData.solutionType"
                :label="$t('global:form.solutions.type')"
                :items="solutionTypeList"
                :validation="$v.formData.solutionType"
              />
              <div v-if="toolTypeSelected === 'program' || formData.solutionType || toolTypeSelected === 'tool'">
                <SelectField
                  v-if="toolTypeSelected === 'program' || formData.solutionType || toolTypeSelected === 'tool'"
                  v-model="formData.survey"
                  :label="$t(surveyName)"
                  :items="foundSurveys"
                  :validation="$v.formData.survey"
                />
                <SelectField
                  v-if="false"
                  v-model="formData.learningPath"
                  :label="$t('global:form.learning.path')"
                  :items="paths"
                  :disabled="disabledSolution"
                  multiple
                  :select-all-option="true"
                  :searcheable="true"
                  @change="getSolutionsLearningPath"
                />
                <SelectField
                  v-if="toolTypeSelected === 'solution'"
                  v-model="formData.solutions"
                  :label="$t('global:form.solution')"
                  :items="filteredSolutionsList"
                  :disabled="disabledSolution"
                  :validation="$v.formData.solutions"
                  multiple
                  :select-all-option="true"
                  :searcheable="true"
                />
                <SelectField
                  v-if="toolTypeSelected === 'program'"
                  v-model="formData.programs"
                  :label="$t('global:menu.programs')"
                  :items="filteredProgramsList"
                  :disabled="disabledProgram"
                  :validation="$v.formData.programs"
                  multiple
                  :select-all-option="true"
                  :searcheable="true"
                />

                <SelectField
                  v-if="toolTypeSelected === 'tool'"
                  v-model="formData.tools"
                  label="Ferramentas"
                  :items="filteredToolsList"
                  :disabled="disabledProgram"
                  :validation="$v.formData.tools"
                  multiple
                  :select-all-option="true"
                  :searcheable="true"
                />
              </div>
            </div>
            <div v-if="showDatepicker">
              <Datepicker
                v-model="formData.dateInit"
                :label="$t('global:form.filters.date.init')"
                :validation="$v.formData.dateInit"
              />
              <Datepicker
                v-model="formData.dateEnd"
                :label="$t('global:form.filters.date.end')"
                :validation="$v.formData.dateEnd"
              />
            </div>
          </FormSection>
          <Action
            slot="button"
            primary
            large
            fixed-width
            type="button"
            :text="$t('reports.generate:btn.generate')"
            @click="getReportSurveyList"
          />
        </form>
      </div>
    </div>
    <ModalConfirm
      :active="modalWarning"
      :title="$t('reports:report.survey.answers.list.title')"
      :description="$t('reports.generate:report.modal.description')"
      @cancelAction="closeModal"
      @confirmAction="generateReport"
    />
  </div>
</template>

<style src="@/assets/styles/themes/default/solutions.css"></style>
